/* Live-only toast queue — shows new notifications while the user is on the site.
   Auto-dismisses after 6s. Historical items go to the bell + Notification Center. */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileCheck, AlertTriangle, CreditCard, Info, X, ExternalLink, Loader2,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import "./NotificationToaster.css";

const iconFor = (n) => {
  const map = {
    "file-check": FileCheck, "alert-triangle": AlertTriangle,
    "credit-card": CreditCard, "info": Info, "loader": Loader2,
  };
  return map[n.icon] || (n.kind === "billing" ? CreditCard : n.kind === "system" ? Info : FileCheck);
};

const fmtDuration = (ms) => {
  if (!ms || ms <= 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
};

export default function NotificationToaster() {
  const nav = useNavigate();
  const { toasts, dismissToast, markRead } = useNotifications();

  // Auto-dismiss each new toast after 6s
  useEffect(() => {
    const timers = toasts.map((t) => setTimeout(() => dismissToast(t.id), 6000));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toasts.length]);

  if (!toasts.length) return null;

  const handleOpen = async (n) => {
    if (!n.read_at) await markRead(n.id);
    dismissToast(n.id);
    if (n.action_url) nav(n.action_url);
  };

  return (
    <div className="nt" data-testid="notification-toaster">
      {toasts.slice(-3).map((n) => {
        const Icon = iconFor(n);
        const meta = n.metadata || {};
        const isStage = !!meta.stage;
        const church = meta.church;
        const occasion = meta.occasion;
        const genTime = fmtDuration(meta.generation_ms);
        const compact = !!(church || occasion || genTime);
        return (
          <div
            key={n.id}
            className={`nt-toast p-${n.priority} ${isStage ? "is-stage" : ""}`}
            data-testid={`toast-${n.id}`}
          >
            <span className="nt-i">
              <Icon size={16} className={n.icon === "loader" ? "nt-spin" : ""} />
            </span>
            <div className="nt-body">
              <div className="nt-title">{n.title}</div>
              {compact ? (
                <div className="nt-lines">
                  {church && <div className="nt-line strong">{church}</div>}
                  {occasion && <div className="nt-line">{occasion}</div>}
                  {genTime && <div className="nt-line muted">Generated in {genTime}</div>}
                </div>
              ) : (
                n.body && <div className="nt-blurb">{n.body}</div>
              )}
              {n.action_url && !isStage && (
                <button className="nt-cta" onClick={() => handleOpen(n)} data-testid={`toast-open-${n.id}`}>
                  Open Report <ExternalLink size={11} />
                </button>
              )}
            </div>
            <button
              className="nt-x"
              onClick={() => dismissToast(n.id)}
              aria-label="Dismiss"
              data-testid={`toast-dismiss-${n.id}`}
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
